/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { jam } from '@jam.dev/sdk';
import { User } from '@auth0/auth0-spa-js';
import { ITenant, IUser } from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { useEffect, useState } from 'react';
// eslint-disable-next-line no-restricted-imports
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { onHeapIdentify } from '../services/heap';
import {
  ENABLE_LAUNCHDARKLY,
  initLaunchDarklyContext,
} from '../services/launchDarkly';
import { logger } from '../services/logger';
import Sentry from '../services/sentry';

interface InitThirdPartyContextArgs {
  ldClient: ReturnType<typeof useLDClient>;
  /** The tenant-scoped user record (from `useCurrentUser`). */
  currentTenantUser: Maybe<IUser>;
  currentTenant: Maybe<ITenant>;
  /** The authenticated Auth0 user. */
  authUser: Maybe<User>;
  userId: Maybe<string>;
}

/**
 * Initializes our third-party integrations with the current user/tenant
 * identity — LaunchDarkly context, Heap, Sentry, and Jam — and reports whether
 * LaunchDarkly is still loading (the value used to gate route rendering).
 *
 * The `isLDLoading` gate opens as soon as the identified context's flags are
 * in, from whichever of these happens first:
 * - the SDK's `'change'` event. The LD React provider updates the flags
 *   returned by `useFlags()` in its own `'change'` listener; emitting
 *   `'change'` calls every listener synchronously, so the provider's flag
 *   setState and our `setIsLDLoading(false)` land in the same React 18 batch
 *   (single commit). This guarantees the route tree never renders with the
 *   gate open while the flags are still the stale anonymous-context values.
 * - `identify()` resolving. The SDK only emits `'change'` when a flag value
 *   actually differs from the anonymous context, so without this the gate
 *   would sit on the backstop timer whenever the values happen to match.
 * A backstop timer still covers an `identify()` that never settles.
 */
export const useInitThirdPartyContext = ({
  ldClient,
  currentTenantUser,
  currentTenant,
  authUser,
  userId,
}: InitThirdPartyContextArgs): boolean => {
  const [isLDLoading, setIsLDLoading] = useState(true);
  const tenantId = currentTenant?.id || '';

  // Jam metadata updates eagerly with whatever identity is available — it does
  // not wait for the full tenant-user record like the integrations below.
  useEffect(() => {
    try {
      jam.metadata(() => ({
        tenantId,
        tenantName: currentTenant?.name,
        testTenant: currentTenant?.testTenant,
        userEmail: authUser?.email,
        userName: authUser?.name,
        userId,
      }));
    } catch (err) {
      logger.warn(err, 'Error setting Jam metadata');
    }
  }, [
    currentTenant?.name,
    currentTenant?.testTenant,
    tenantId,
    authUser?.email,
    authUser?.name,
    userId,
  ]);

  useEffect(() => {
    if (!currentTenantUser || !currentTenant) {
      return;
    }

    // When LaunchDarkly is disabled (local dev), initLaunchDarklyContext() is a
    // no-op, so no 'change' ever fires. `useFlags()` serves static defaults
    // there, so there is nothing to gate on — open immediately instead of
    // waiting out the backstop.
    if (!ENABLE_LAUNCHDARKLY) {
      setIsLDLoading(false);
      return;
    }

    setIsLDLoading(true);

    // Safety net for an identify() that neither resolves nor rejects
    const backstop = setTimeout(handleFlagsChanged, 1500);

    function handleFlagsChanged() {
      setIsLDLoading(false);
      clearTimeout(backstop);
      // The gate only needs to open once; stop listening so later flag updates
      // don't keep re-invoking this (cleanup still handles the unmount case).
      ldClient?.off('change', handleFlagsChanged);
    }
    ldClient?.on('change', handleFlagsChanged);
    (async () => {
      try {
        Sentry.getGlobalScope().setAttributes({
          userId: currentTenantUser.id,
          tenantId: currentTenant.id,
        });
        await initLaunchDarklyContext(
          ldClient,
          currentTenantUser,
          currentTenant,
        );
        // identify() has resolved, so the flags are in whether or not any
        // value changed (no 'change' fires when none did)
        handleFlagsChanged();
        onHeapIdentify(currentTenantUser.email, {
          id: currentTenantUser.id!,
          name: currentTenantUser.name,
          tenantId: currentTenant.id!,
          tenantName: currentTenant.description!,
        });
      } catch (err) {
        // Even if LaunchDarkly fails, we should not block the app.
        logger.log(err, 'Error in initLaunchDarklyContext');
        handleFlagsChanged();
      }
    })();

    return () => {
      ldClient?.off('change', handleFlagsChanged);
      clearTimeout(backstop);
    };
  }, [currentTenantUser, ldClient, currentTenant]);

  return isLDLoading;
};
